<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  hideStatusFilter: { type: Boolean, default: false },
  // Kept in the contract: callers still pass it, and the D2 header may need
  // side-aware popover alignment again.
  // eslint-disable-next-line vue/no-unused-properties
  isOnExpandedLayout: { type: Boolean, required: true },
  // The size of the list as rendered, already narrowed to the active scope by
  // the caller - the meta counts it picks from are not interchangeable.
  conversationCount: { type: Number, required: true },
  isListLoading: { type: Boolean, required: true },
  showFilterPanel: { type: Boolean, default: false },
  showSaveViewPanel: { type: Boolean, default: false },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
]);

const { t } = useI18n();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

// D2 header: no permanent status pill. A removable chip appears only when the
// list deviates from the default Open view; × returns to Open.
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const chatStatusFilter = useMapGetter('getChatStatusFilter');
const currentStatus = computed(
  () => chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN
);
// `all` is the list's default status, so it earns no chip — only a deliberate
// narrowing (open / resolved / pending / snoozed) is worth announcing.
const showStatusChip = computed(
  () => currentStatus.value !== wootConstants.STATUS_TYPE.ALL
);
const statusChipLabel = computed(() =>
  t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${currentStatus.value}.TEXT`)
);
const resetStatusToDefault = () => {
  const all = wootConstants.STATUS_TYPE.ALL;
  emit('basicFilterChange', all, 'status');
  store.dispatch('setChatStatusFilter', all);
  updateUISettings({
    conversations_filter_by: {
      ...(uiSettings.value.conversations_filter_by || {}),
      status: all,
    },
  });
};

// The filter and save-view panels are far wider than the conversation list
// column, which clips its overflow, so their teleport targets are pinned to
// the trigger from outside the column instead of placed inside it.
const filterTriggerRef = ref(null);
const filterTargetRef = ref(null);
const saveViewTriggerRef = ref(null);
const saveViewTargetRef = ref(null);

const isFilterPanelOpen = computed(() => props.showFilterPanel);
const isSaveViewPanelOpen = computed(() => props.showSaveViewPanel);

const { fixedPosition: filterPosition, updatePosition: updateFilterPosition } =
  useDropdownPosition(filterTriggerRef, filterTargetRef, isFilterPanelOpen);

const {
  fixedPosition: saveViewPosition,
  updatePosition: updateSaveViewPosition,
} = useDropdownPosition(
  saveViewTriggerRef,
  saveViewTargetRef,
  isSaveViewPanelOpen
);

// The target is empty until its panel teleports in, so measure after it lands.
watch([isFilterPanelOpen, isSaveViewPanelOpen], async () => {
  await nextTick();
  updateFilterPosition();
  updateSaveViewPosition();
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const formattedConversationCount = computed(() =>
  formatNumber(props.conversationCount)
);
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 px-3.5 h-12 bg-n-surface-1"
    :class="{
      'border-b border-n-weak': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-baseline min-w-0 gap-1.5">
      <h1
        class="m-0 text-base font-semibold truncate text-n-slate-12"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </h1>
      <span
        v-if="conversationCount > 0 && !isListLoading"
        class="text-xs tabular-nums text-n-slate-10 shrink-0"
        :title="conversationCount"
        data-testid="conversation-count"
      >
        {{ formattedConversationCount }}
      </span>
    </div>
    <div class="flex items-center gap-0.5 shrink-0">
      <button
        v-if="
          showStatusChip &&
          !hasAppliedFiltersOrActiveFolders &&
          !hideStatusFilter
        "
        type="button"
        data-testid="status-chip"
        class="flex items-center h-6 gap-1 rounded-full bg-n-violet-3 px-2.5 text-xs font-medium text-n-violet-11 hover:bg-n-violet-4"
        :aria-label="$t('CHAT_LIST.STATUS_MENU.TOOLTIP')"
        @click="resetStatusToDefault"
      >
        <span class="truncate max-w-24">{{ statusChipLabel }}</span>
        <span class="i-lucide-x size-3" />
      </button>
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div ref="saveViewTriggerRef" class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            ghost
            @click="emit('addFolders')"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          ghost
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div ref="filterTriggerRef" class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            ghost
            @click="emit('filtersModal')"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          ghost
          @click="emit('deleteFolders')"
        />
      </template>
      <div v-else ref="filterTriggerRef" class="relative">
        <button
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          type="button"
          :aria-label="$t('FILTER.TOOLTIP_LABEL')"
          class="grid rounded-full size-7 shrink-0 place-items-center bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
          @click="emit('filtersModal')"
        >
          <span class="i-lucide-list-filter size-4" />
        </button>
      </div>
      <!-- Extra per-view actions (e.g. the tickets layout toggle) inline. -->
      <slot name="actions" />
      <!-- The Display popover: status + sort together, behind one icon. -->
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :hide-status-filter="hideStatusFilter"
        @change-filter="onBasicFilterChange"
      />
    </div>
    <TeleportWithDirection>
      <div
        id="conversationFilterTeleportTarget"
        ref="filterTargetRef"
        :class="filterPosition.class"
        :style="filterPosition.style"
      />
      <div
        id="saveFilterTeleportTarget"
        ref="saveViewTargetRef"
        :class="saveViewPosition.class"
        :style="saveViewPosition.style"
      />
    </TeleportWithDirection>
  </div>
</template>
